import { useState, useCallback } from 'react';
import { QueryClient, useIsMutating, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { compact } from 'lodash';
import { useModal } from 'mui-modal-provider';

import { Post } from '../Utils/Types/Posts';
import queriesConfig from '../Services/queriesConfig';
import useSpace from './useSpace';
import {
  extractErrorsForNestedAttributes,
  extractErrorForToaster,
} from '../Services/ApiErrorHelpers';
import Analytics from '../Services/AnalyticsService';
import { PostTypes } from '../Utils/Types';
import { isIdea, isMessage } from '../Utils/Post';
import { editPostPath, postPath } from '../Utils/routes';
import { messages, moment, currentLocale } from '../I18n';
import { Space } from '../Utils/Types/Space';
import ModerationWarningModal from '../Components/Posts/Moderation/ModerationWarningModal';
import { notify } from '../Utils/NotificationUtils';

interface PostMutationSuccessProps {
  response: any;
  space: Space;
  queryClient: QueryClient;
  data: any;
  responsePost: Post;
  refetch: any;
  navigate: any;
  params: any;
  intl: any;
  notifications: any;
  onSuccess?: (response: any) => void;
}

function onCreatePostSuccess({
  space,
  queryClient,
  data,
  response,
  responsePost,
  refetch,
  navigate,
  params,
  intl,
  notifications,
  onSuccess,
}: PostMutationSuccessProps) {
  Analytics.track('Post', `Create ${data.post_type}`, {
    'Post Type': data.post_type,
    'Event Source': params.get('event_source'),
  });

  if (isIdea(responsePost.type) || isMessage(responsePost.type)) {
    if (!responsePost.published_at && !isIdea(responsePost.type)) {
      notify(notifications, intl.formatMessage({ id: 'POST_FORM.POST_SAVED' }));
    }

    if (responsePost.published_at) {
      navigate(`${postPath(space.slug, responsePost.slug)}?modal=post-created`);
    } else {
      navigate(postPath(space.slug, responsePost.slug));
    }
  } else {
    notify(notifications, intl.formatMessage({ id: 'POST_FORM.POST_SAVED' }));
    navigate(editPostPath(space.slug, responsePost.slug, responsePost.type));
  }

  refetch();
  queryClient.invalidateQueries(['post-children']);

  if (onSuccess) {
    onSuccess(response);
  }
}

function onUpdatePostSuccess({
  response,
  queryClient,
  data,
  responsePost,
  intl,
  notifications,
  onSuccess,
}: PostMutationSuccessProps) {
  Analytics.track('Post', 'Update', `Update ${data.post_type}`, {
    'Post Type': data.post_type,
  });

  const { ask_to_publish_at: askToPublishAt, published_at: publishedAt, type } = responsePost;
  let toasterTextId = null;
  let toasterValueOptions = {};

  // TODO: Refactor this to be more readable, and be reusable for the create case
  if (type === PostTypes.IDEA) {
    toasterTextId = 'POST_FORM.POST_SAVED';
  } else if (publishedAt) {
    toasterTextId = 'POST_FORM.POST_PUBLISHED';
  } else if (askToPublishAt) {
    const momentDefaultDate = messages[currentLocale]['MOMENT.DATE.DEFAULT'];

    toasterTextId = 'POST_FORM.POST_SCHEDULED';
    toasterValueOptions = {
      date: moment(askToPublishAt).format(momentDefaultDate),
      time: moment(askToPublishAt).format('LT'),
    };
  } else {
    toasterTextId = 'POST_FORM.POST_SAVED';
  }

  notify(notifications, intl.formatMessage({ id: toasterTextId }, { ...toasterValueOptions }));

  queryClient.setQueryData(queriesConfig.getPost(responsePost.id).queryKey, response);

  if (onSuccess) {
    onSuccess(response);
  }
}

export default function usePostMutation({
  post,
  isEdit = false,
  isDashboard = false,
  intl,
  notifications,
}: {
  post?: Post;
  isEdit?: boolean;
  isDashboard?: boolean;
  intl: any;
  notifications: any;
}) {
  const queryClient = useQueryClient();
  const { space, refetch } = useSpace();
  const navigate = useNavigate();
  const [errors, setErrors] = useState<any>({});
  const mutationKey = isEdit ? compact(['updatePost', post?.id]) : ['createPost'];
  const { showModal } = useModal();

  const showModerationModal = (post: Post, handleClose: any) => {
    showModal(ModerationWarningModal, {
      post,
      isEdit,
      handleClose,
    });
  };

  // this is to know if a mutation is happening on another hook call
  const postMutationsInProgress = useIsMutating(mutationKey);
  const isMutating = postMutationsInProgress > 0;

  const params = new URLSearchParams(window.location.search);

  let method: any;
  if (isEdit && post?.id) {
    method = isDashboard
      ? queriesConfig.updatePostDashboard(post.id).mutationFn
      : queriesConfig.updatePost(post.id).mutationFn;
  } else {
    method = queriesConfig.createPost().mutationFn;
  }

  const mutation = useMutation(method, { mutationKey });

  const mutate = useCallback(
    (data: any, { onSuccess }: { onSuccess?: (response: any) => void } = {}) => {
      return mutation.mutate(data, {
        onSuccess: (response: any) => {
          if (response.ok) {
            setErrors({});
            const responsePost = response.data?.data;

            // Once the save is successful
            // we need to trigger some actions like (show toaster, update cache, navigate to the post, etc)
            const successParams = {
              space,
              queryClient,
              data,
              responsePost,
              response,
              refetch,
              navigate,
              params,
              intl,
              notifications,
              onSuccess,
            };

            if (responsePost?.meta?.ai_data?.moderation) {
              // If the post is under moderation, we show the moderation modal
              showModerationModal(
                responsePost,
                post?.id
                  ? () => onUpdatePostSuccess(successParams)
                  : () => onCreatePostSuccess(successParams),
              );
            } else if (post?.id) {
              onUpdatePostSuccess(successParams);
            } else {
              onCreatePostSuccess(successParams);
            }
          } else if (response.status === 422) {
            const apiErrors = extractErrorsForNestedAttributes(response);
            notify(
              notifications,
              intl.formatMessage({ id: 'POST_FORM.ERRORS.FILL_FIELDS' }),
              'error',
            );

            setErrors(apiErrors);
          } else {
            notify(notifications, extractErrorForToaster(response, intl), 'error');
          }
        },
      });
    },
    [],
  );

  return {
    ...mutation,
    mutate,
    isLoading: mutation.isLoading || isMutating,
    errors,
  };
}
